import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Download, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_LABEL = { active: "Live", launching: "Launching", coming_soon: "Coming soon" };

/** Register-your-interest sign-ups per coming-soon category: supplier count first, buyer count second, both lists below. */
export default function CategoryInterestPanel() {
  const [data, setData] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [list, setList] = useState("supplier");

  const load = () => api.get("/admin/category-interest").then(({ data }) => setData(data)).catch(() => setData({ summary: [], suppliers: [], buyers: [], totals: { suppliers: 0, buyers: 0 } }));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const src = list === "supplier" ? data?.suppliers : data?.buyers;
    return (src || []).filter((r) => !catFilter || r.category_id === catFilter);
  }, [data, list, catFilter]);

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.email} from the ${list} list for ${r.category_name}?`)) return;
    try { await api.delete(`/admin/category-interest/${list}/${r.id}`); toast.success("Removed"); load(); } catch { toast.error("Couldn't remove"); }
  };

  const exportCsv = async () => {
    const res = await api.get(`/admin/category-interest/export.csv?role=${list}${catFilter ? `&category_id=${catFilter}` : ""}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `category-interest-${list}s.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-sm text-[#7D8C85]">Loading…</div>;
  const best = Math.max(0, ...data.summary.map((c) => c.suppliers));

  return (
    <section className="bg-white border border-[#E8ECE9] p-6 mb-6" data-testid="category-interest-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="tracking-overline mb-1">Coming-soon categories</div>
          <h3 className="font-serif text-xl text-[#11211A]">Register-your-interest sign-ups</h3>
          <p className="text-sm text-[#7D8C85] mt-1">Suppliers queued to list vs buyers waiting, per category. The supplier count is what tells you a category is ready to launch.</p>
        </div>
        <div className="text-sm text-[#4A5550]" data-testid="category-interest-totals"><strong className="text-[#11211A]">{data.totals.suppliers}</strong> suppliers · {data.totals.buyers} buyers</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="category-interest-summary">
        {data.summary.map((c) => {
          const lead = c.suppliers > 0 && c.suppliers === best && c.launch_status === "coming_soon";
          return (
            <button type="button" key={c.id} onClick={() => setCatFilter(catFilter === c.id ? "" : c.id)}
              className={`text-left p-4 border rounded-md transition-colors ${catFilter === c.id ? "border-[#264839] bg-[#264839]/5" : "border-[#E8ECE9] hover:border-[#C4CFC9]"} ${lead ? "ring-1 ring-[#264839]" : ""}`}
              data-testid={`category-interest-card-${c.slug}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">{STATUS_LABEL[c.launch_status] || c.launch_status}{lead ? " · most suppliers queued" : ""}</div>
              <div className="text-sm font-medium text-[#11211A] mt-1 leading-snug line-clamp-2">{c.name}</div>
              <div className="mt-3 flex items-baseline gap-3">
                <div><span className="font-serif text-3xl text-[#264839]" data-testid={`category-interest-suppliers-${c.slug}`}>{c.suppliers}</span><span className="text-[11px] text-[#7D8C85] ml-1">suppliers</span></div>
                <div><span className="text-base text-[#4A5550]" data-testid={`category-interest-buyers-${c.slug}`}>{c.buyers}</span><span className="text-[11px] text-[#7D8C85] ml-1">buyers</span></div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <div className="flex border border-[#C4CFC9] rounded-sm overflow-hidden text-xs">
          <button type="button" onClick={() => setList("supplier")} className={`px-3 py-1.5 inline-flex items-center gap-1 ${list === "supplier" ? "bg-[#264839] text-white" : "text-[#4A5550]"}`} data-testid="category-interest-tab-suppliers"><Building2 className="w-3.5 h-3.5" /> Suppliers ({(data.suppliers || []).filter((r) => !catFilter || r.category_id === catFilter).length})</button>
          <button type="button" onClick={() => setList("buyer")} className={`px-3 py-1.5 inline-flex items-center gap-1 ${list === "buyer" ? "bg-[#264839] text-white" : "text-[#4A5550]"}`} data-testid="category-interest-tab-buyers"><Search className="w-3.5 h-3.5" /> Buyers ({(data.buyers || []).filter((r) => !catFilter || r.category_id === catFilter).length})</button>
        </div>
        {catFilter && <button type="button" onClick={() => setCatFilter("")} className="text-xs text-[#B35C4A] hover:underline" data-testid="category-interest-clear-filter">Clear category filter</button>}
        <button type="button" onClick={exportCsv} className="btn-secondary text-xs ml-auto inline-flex items-center gap-1" data-testid="category-interest-export"><Download className="w-3.5 h-3.5" /> Export {list}s CSV</button>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550] text-left text-xs uppercase tracking-wider">
            <tr><th className="px-3 py-2 font-medium">Registered</th><th className="px-3 py-2 font-medium">Category</th><th className="px-3 py-2 font-medium">{list === "supplier" ? "Company" : "Name"}</th><th className="px-3 py-2 font-medium">Email</th><th className="px-3 py-2 font-medium">{list === "supplier" ? "Supplies" : "Looking to source"}</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody data-testid={`category-interest-list-${list}`}>
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#7D8C85]" data-testid="category-interest-empty">No {list} sign-ups yet{catFilter ? " for this category" : ""}.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E8ECE9] align-top" data-testid={`category-interest-row-${r.id}`}>
                <td className="px-3 py-2 text-xs text-[#7D8C85] whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs text-[#4A5550]">{r.category_name}</td>
                <td className="px-3 py-2 text-[#11211A]">{list === "supplier" ? r.company : r.name}</td>
                <td className="px-3 py-2 text-[#4A5550] break-all">{r.email}</td>
                <td className="px-3 py-2 text-xs text-[#4A5550] max-w-md">{r.details}</td>
                <td className="px-3 py-2"><button type="button" onClick={() => remove(r)} className="text-[#B35C4A] hover:text-[#8E4536]" title="Remove" data-testid={`category-interest-remove-${r.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
